import moment from 'moment';
import momentTz from 'moment-timezone';
import { RangeModifier } from 'react-day-picker';
import { ALERT_FREQUENCY_UNITS } from 'types/machine-health/alerts';
import { TimeRange } from 'types/machine-management';

function pad(num: number) {
  return `${num}`.padStart(2, '0');
}

type DateStyle =
  | 'short'
  | 'short-year4'
  | 'long'
  | 'time-elapsed'
  | 'hours-minutes'
  | 'hours-minutes-seconds'
  | 'day-of-week'
  | 'numeric-date-time'
  | 'numeric-month-day'
  | 'day-month-year'
  | 'day-month-short-year'
  | 'day-month-ordinal-time';

type TimeUnit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month';

// adpated from
// https://stackoverflow.com/questions/19700283/how-to-convert-time-in-milliseconds-to-hours-min-sec-format-in-javascript/67462589#67462589
function formatMilliseconds(milliseconds: number, padStart: boolean): string {
  const asSeconds = milliseconds / 1000;

  let days = undefined;
  let hours = undefined;
  let minutes = Math.floor(asSeconds / 60);
  const seconds = Math.floor(asSeconds % 60);

  if (minutes > 59) {
    hours = Math.floor(minutes / 60);
    minutes %= 60;
  }

  if (hours !== undefined && hours > 24) {
    days = Math.floor(hours / 24);
    hours %= 24;
  }

  if (days !== undefined && hours !== undefined)
    return `${days} days ${padStart ? pad(hours) : hours}:${pad(minutes)}:${pad(seconds)}`;
  else if (hours !== undefined)
    return `${padStart ? pad(hours) : hours}:${pad(minutes)}:${pad(seconds)}`;
  else return `${padStart ? pad(minutes) : minutes}:${pad(seconds)}`;
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return `${day}th`;
  }
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

export function formatDate(
  date?: Date | number | string,
  style: DateStyle = 'short',
  timeZone?: string | null,
  locale?: string,
  is12Hour?: boolean
): string {
  if (!date) {
    return '';
  }
  if (typeof date === 'string') {
    date = new Date(date);
  }
  if (typeof date === 'number') {
    return formatMilliseconds(date.valueOf(), true);
  }

  const userLocale = locale ? [locale] : [];

  // A machine's time zone can be null when unconfigured
  const coalescedTimeZone = timeZone ?? undefined;

  switch (style) {
    case 'short-year4':
      return date.toLocaleDateString(userLocale, {
        year: 'numeric',
        day: '2-digit',
        month: '2-digit',
        timeZone: coalescedTimeZone
      });
    case 'short':
      return date.toLocaleDateString(userLocale, {
        year: '2-digit',
        day: '2-digit',
        month: '2-digit',
        timeZone: coalescedTimeZone
      });
    case 'long':
      return date.toLocaleDateString(userLocale, {
        year: 'numeric',
        day: '2-digit',
        month: '2-digit',
        timeZone: coalescedTimeZone
      });
    case 'hours-minutes':
      return date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: 'numeric',
        timeZone: coalescedTimeZone,
        hour12: is12Hour
      });
    case 'hours-minutes-seconds':
      return date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZone: coalescedTimeZone,
        hour12: is12Hour
      });
    case 'time-elapsed':
      return formatMilliseconds(date.valueOf(), true);
    case 'day-of-week':
      return date.toLocaleDateString(userLocale, {
        weekday: 'short',
        year: '2-digit',
        day: 'numeric',
        month: 'short',
        timeZone: coalescedTimeZone
      });
    case 'day-month-year':
      return date.toLocaleDateString(userLocale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: coalescedTimeZone
      });
    case 'day-month-short-year':
      return date.toLocaleDateString(userLocale, {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
        timeZone: coalescedTimeZone
      });
    case 'numeric-date-time':
      return date.toLocaleDateString(userLocale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: coalescedTimeZone
      });
    case 'numeric-month-day':
      return date.toLocaleDateString(userLocale, {
        month: '2-digit',
        day: '2-digit',
        timeZone: coalescedTimeZone
      });
    case 'day-month-ordinal-time': {
      const optionsDate = {
        weekday: 'short',
        timeZone: coalescedTimeZone
      };
      const optionsMonth = {
        month: 'short',
        timeZone: coalescedTimeZone
      };
      const optionsTime = {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZone: coalescedTimeZone,
        hour12: is12Hour
      };
      const daySuffix = getDaySuffix(date.getDate());
      const formattedDate = date.toLocaleDateString(userLocale, optionsDate);
      let formattedTime = date.toLocaleTimeString(userLocale, optionsTime);
      const formattedMonth = date.toLocaleDateString(userLocale, optionsMonth);
      formattedTime = formattedTime.toUpperCase(); // Convert to uppercase
      return `${formattedDate.replace(',', '')} ${formattedMonth} ${daySuffix} ${formattedTime}`;
    }
  }
}

type DurationFormat = 'hours:mins:secs' | 'days hrs mins secs' | 'hours:mins';

/**
 * Format a number of milliseconds into a string of given format
 * @param durationMillis - the duration in milliseconds
 * @param format - the format to use
 */
export const formatDuration = (durationMillis: number, format: DurationFormat): string => {
  const mDuration = moment.duration(durationMillis, 'milliseconds');

  switch (format) {
    // e.g. 45:00:12
    case 'hours:mins:secs':
      return `${pad(Math.floor(mDuration.asHours()))}:${pad(mDuration.minutes())}:${pad(
        mDuration.seconds()
      )}`;
    // e.g. 45:00
    case 'hours:mins':
      return `${pad(Math.floor(mDuration.asHours()))}:${pad(mDuration.minutes())}`;
    case 'days hrs mins secs':
      return [
        Math.floor(mDuration.days()) > 0 ? `${Math.floor(mDuration.days())} days` : '',
        Math.floor(mDuration.hours()) > 0 ? `${Math.floor(mDuration.hours())} hrs` : '',
        Math.floor(mDuration.minutes()) > 0 ? `${mDuration.minutes()} mins` : '',
        Math.floor(mDuration.seconds()) > 0 ? `${mDuration.seconds()} secs` : ''
      ]
        .filter((a) => a) // drop empty strings
        .join(' ')
        .trim();

    default:
      return '';
  }
};

/**
 * Format a date string to match ISO 8601 date format.
 * This function is needed because Date's toISOString() output
 * does not quite match ISO 8601 and Python's datetime.
 *
 * For example: for Jan 27, 2022, 5:16pm in EST (GMT-5)
 * toISOString() of this date produces -> 2022-01-27T22:16:29.787Z
 *     **note the adjustment to GMT, the Z instead of timezone, and milliseconds (.787)
 *
 * This function outputs the expected YYYY-MM-DDTHH:mm:ssZ format.
 * @param date - a date object to be converted to a string with ISO 8601 format.
 * @param tz - if provided, datetime would be converted to the appropriate TZ
 * e.g machine in 'America/Chicago' time zone, "2022-07-05 00:00" it would be  would be converted to "2022-07-05T00:00:00-05:00"
 */
export const toISO8601 = (date: Date, tz?: string): string => {
  if (tz) {
    const utc = moment(date).utc(true);
    return momentTz.tz(utc.format('YYYY-MM-DD HH:mm:ss'), tz).format();
  }
  return moment(date).format('YYYY-MM-DDTHH:mm:ssZ');
};

export const toCustom = (date: Date): string => {
  return moment(date).format('YYYY-MM-DDTHH:mm:ss');
};

/**
 * Return a new date range by checking if the given day is closer
 * to one day or the other.
 *
 * Should only be called if the dates are between the range endpoints.
 *
 * @param day - A date object that is the new day from which to generate the range
 * @param from - A date object specifying the current "from" day in the range
 * @param to - A date object specifying the current "to" day in the range
 */
export const addDayToRangeBetween = (day: Date, from: Date, to: Date): RangeModifier => {
  const diffFrom = Math.abs(from.getTime() - day.getTime());
  const diffTo = Math.abs(to.getTime() - day.getTime());

  // If closer to the from, replace the from
  if (diffFrom < diffTo) {
    return { from: day, to: to };
  }
  // Otherwise, if closer to the to or equal difference, replace the to
  else {
    return { from: from, to: day };
  }
};

/**
 * Removes hour, minute, second, and millisecond data from a date (for rough comparisons)
 *
 * @param date date object to shorten
 * @returns date object with only day, month, and year
 */
export function getShortDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Calculate duration between to timestamps
 *
 * @param startTimestamp string object to calculate duration
 * @param endTimestamp string object to calculate duration
 * @returns string object with the duration
 */
export const getDurationBetweenTimestamps = (
  startTimestamp: string,
  endTimestamp: string | undefined,
  format?: DurationFormat
): string => {
  let duration = '-';
  if (startTimestamp && endTimestamp) {
    const startDate = new Date(startTimestamp);
    const endDate = new Date(endTimestamp);
    const durationAsMillis = endDate.getTime() - startDate.getTime();
    duration = formatDuration(durationAsMillis, format ? format : 'hours:mins:secs');
  }
  return duration;
};

/* Formats date for the Flyout Details */
export const pmFormatDate = (selectedDay: Date): string => {
  const m = moment(selectedDay);
  return m.format('MMM Do YYYY');
};
export const standardFormatDate = (selectedDay: Date): string => {
  const m = moment(selectedDay);
  return m.format('MM-DD-YYYY');
};

export interface DateRange {
  from?: Date;
  to?: Date;
}

/**
 * Get user timezone
 *
 * @returns string object with the user timezone
 */
export const getUserTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Check if user timezone is from US
 *
 * @returns true if it is a US timezone
 */
export const isUSTimeZone = (timeZone: string): boolean => {
  const usTimezones = [
    'America/Adak',
    'America/Anchorage',
    'America/Atka',
    'America/Boise',
    'America/Chicago',
    'America/Denver',
    'America/Detroit',
    'America/Fort_Wayne',
    'America/Indiana/Indianapolis',
    'America/Indiana/Knox',
    'America/Indiana/Marengo',
    'America/Indiana/Petersburg',
    'America/Indiana/Tell_City',
    'America/Indiana/Vevay',
    'America/Indiana/Vincennes',
    'America/Indiana/Winamac',
    'America/Indianapolis',
    'America/Juneau',
    'America/Kentucky/Louisville',
    'America/Kentucky/Monticello',
    'America/Knox_IN',
    'America/Los_Angeles',
    'America/Louisville',
    'America/Menominee',
    'America/Metlakatla',
    'America/New_York',
    'America/Nome',
    'America/North_Dakota/Beulah',
    'America/North_Dakota/Center',
    'America/North_Dakota/New_Salem',
    'America/Phoenix',
    'America/Shiprock',
    'America/Virgin',
    'America/Yakutat',
    'Pacific/Honolulu',
    'US/Alaska',
    'US/Aleutian',
    'US/Arizona',
    'US/Central',
    'US/East-Indiana',
    'US/Eastern',
    'US/Hawaii',
    'US/Indiana-Starke',
    'US/Michigan',
    'US/Mountain',
    'US/Pacific',
    'US/Pacific-New',
    'US/Samoa'
  ];
  return usTimezones.includes(timeZone);
};

/**
 * Format tooltip
 */
export const formatTooltipDateMessage = (time: Date, timeZone: string | undefined): string => {
  return isUSTimeZone(getUserTimeZone())
    ? `${formatDate(time, 'hours-minutes', timeZone)} ${formatDate(
        time,
        'numeric-month-day',
        timeZone,
        'en-US'
      )}`
    : `${formatDate(time, 'hours-minutes', timeZone)} ${formatDate(
        time,
        'numeric-month-day',
        timeZone
      )}`;
};

/* Convert seconds to HH:MM:SS format */
export const ConvertToHHMMSS = (seconds: number): string => {
  const result = new Date(seconds * 1000).toISOString().slice(11, 19);
  return result;
};

export const ConvertHHMMSStoMinutes = (
  timeStampTarget: string,
  timeStampActual: string
): string => {
  // this approach only works if time difference is less than 24 hours
  let difference = Math.floor(
    moment(timeStampActual, 'HH:mm:ss').diff(moment(timeStampTarget, 'HH:mm:ss'), 'minutes')
  );
  if (isNaN(difference)) {
    const splitTimeTarget = timeStampTarget.split(':');
    const splitTimeActual = timeStampActual.split(':');
    const targetSeconds =
      +splitTimeTarget[0] * 60 * 60 + +splitTimeTarget[1] * 60 + +splitTimeTarget[2];
    const actualSeconds =
      +splitTimeActual[0] * 60 * 60 + +splitTimeActual[1] * 60 + +splitTimeActual[2];
    difference = Math.floor((actualSeconds - targetSeconds) / 60);
  }

  return difference > 0 ? ` (+${difference} min)` : difference < 0 ? ` (${difference} min)` : '';
};

export const AddHHMMSS = (firstTimestamp: string, secondTimestamp: string): string => {
  const first = moment.duration(firstTimestamp, 's');
  const second = moment.duration(secondTimestamp, 's');
  const result = first.add(second);

  const formatted = moment.utc(result.asMilliseconds()).format('HH:mm:ss');
  return formatted;
};
/**
 * Changes the timezone of a date to the local timezone
 * mataining the date & time
 * e.g if date is 2022-08-18T00:00:00-05:00 and the user is located in 'America/Los_Angeles'
 * it will return Thu Aug 18 2022 00:00:00 GMT-0700
 */
export const changeTimeZoneToLocal = (date: string | Date, timeZone?: string): Date => {
  if (typeof date === 'string') {
    return new Date(
      new Date(date).toLocaleString('en-US', {
        timeZone
      })
    );
  }
  return new Date(
    date.toLocaleString('en-US', {
      timeZone
    })
  );
};
export const convertUTCDateTimeToLocalDateTime = (utcdate: string): string => {
  const utcDate = new Date(utcdate + 'Z');
  return formatDate(utcDate.toString(), 'numeric-date-time');
};
/**
 * Converts duration of HH:MM:SS format to hours
 */

export const convertDurationToHours = (durationDate: string): number => {
  const minutes = moment.duration(durationDate).asMinutes();
  return minutes / 60;
};

export const convertToSeconds = (value: number, unit: ALERT_FREQUENCY_UNITS): number | null => {
  const unitMapping: Record<ALERT_FREQUENCY_UNITS, TimeUnit> = {
    SECONDS: 'second',
    MINUTES: 'minute',
    HOURS: 'hour',
    DAYS: 'day',
    WEEKS: 'week',
    MONTHS: 'month'
  };

  const originalUnit = unitMapping[unit];

  const secondsPerUnit: Record<TimeUnit, number> = {
    second: 1,
    minute: 60,
    hour: 3600,
    day: 86400,
    week: 604800,
    month: 2628000 // Assuming an average month of 30.44 days
  };

  if (Object.prototype.hasOwnProperty.call(secondsPerUnit, originalUnit)) {
    // Use type assertion to let TypeScript know that originalUnit is a valid key
    return value * secondsPerUnit[originalUnit as TimeUnit];
  }
  // If the originalUnit is not found, return null
  return null;
};

export function getRelativeDateString(dateString: string): string {
  if (!dateString) return 'Date not available';
  const inputDate = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  // Remove the time part for comparison
  inputDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: '2-digit'
  };

  const formattedDate = inputDate.toLocaleDateString('en-us', options);

  if (inputDate.getTime() === today.getTime()) {
    return `Today - ${formattedDate}`;
  } else if (inputDate.getTime() === yesterday.getTime()) {
    return `Yesterday - ${formattedDate}`;
  } else {
    return formattedDate;
  }
}
type Data = {
  updatedAt: string;
};
type key = 'updatedAt';
export const filterByTimeRange = <T extends Data>(
  data: T[],
  timeRange: TimeRange,
  key: key
): T[] => {
  const now = new Date();
  let timeLimit: Date;

  switch (timeRange) {
    case '24_hours':
      timeLimit = new Date(now.getTime() - 24 * 60 * 60 * 1000); // 24 hours in milliseconds
      break;
    case '3_days':
      timeLimit = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000); // 3 days in milliseconds
      break;
    case '7_days':
      timeLimit = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
      break;
    default:
      throw new Error('Invalid time range');
  }

  return data.filter((entry) => {
    const updatedAt = new Date(entry[key]);
    return updatedAt >= timeLimit;
  });
};
